import { Component } from '@theme/component';

/**
 * A size chart trigger + modal popup.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} openButton
 * @property {HTMLElement} popup
 * @property {HTMLElement} overlay
 * @property {HTMLButtonElement} closeButton
 *
 * Usage (rendered by blocks/size-chart.liquid):
 * <size-chart>
 *   <button ref="openButton">...</button>
 *   <div ref="popup">
 *     <div ref="overlay"></div>
 *     <div class="size-chart-modal">
 *       <button ref="closeButton">×</button>
 *       ...
 *     </div>
 *   </div>
 * </size-chart>
 */
export class SizeChart extends Component {
  /** @type {Refs} */
  refs;

  #boundKeydown = this.#handleKeydown.bind(this);

  connectedCallback() {
    super.connectedCallback();

    this.refs.openButton?.addEventListener('click', this.#open.bind(this));
    this.refs.overlay?.addEventListener('click', this.#close.bind(this));
    this.refs.closeButton?.addEventListener('click', this.#close.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.#boundKeydown);
  }

  #open() {
    const { popup } = this.refs;
    if (!popup) return;

    popup.classList.add('active');
    popup.setAttribute('aria-hidden', 'false');
    document.addEventListener('keydown', this.#boundKeydown);

    // Move focus into the modal for accessibility.
    this.refs.closeButton?.focus();
  }

  #close() {
    const { popup } = this.refs;
    if (!popup) return;

    popup.classList.remove('active');
    popup.setAttribute('aria-hidden', 'true');
    document.removeEventListener('keydown', this.#boundKeydown);

    this.refs.openButton?.focus();
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown(event) {
    if (event.key === 'Escape') this.#close();
  }
}

if (!customElements.get('size-chart')) {
  customElements.define('size-chart', SizeChart);
}